// LiveFunnel.js — sección "Live Funnel" del panel.
//
// Panel EN VIVO: qué está pasando AHORA MISMO en las tiendas del comerciante.
// Se refresca solo por polling contra un único endpoint (GET /api/funnel/live),
// que devuelve todo lo que esta página pinta en una sola respuesta.
//
// PRINCIPIO: esta página no calcula métricas propias. Los nombres de campo y la
// semántica salen TAL CUAL de backend/src/routes/funnel.js; si aquí apareciera
// un número que el backend no manda, sería inventado. Cuando un dato no viene o
// no es calculable (p. ej. conversión con 0 checkouts) se muestra "--", nunca un
// cero maquillado como si fuera una medición.
//
// POLLING (los detalles importan: el operador deja esto abierto horas):
//   • 10 s por defecto, pausable.
//   • Nunca se lanza una petición si la anterior sigue en vuelo.
//   • Con la pestaña oculta (document.hidden) NO se hace polling; al volver al
//     primer plano se refresca de inmediato para que "actualizado hace Xs" no
//     mienta.
//   • Las respuestas viejas (cambio de ventana/tienda a mitad de vuelo) se
//     descartan por número de secuencia.

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import createGlobe from 'cobe';
import { G, fmt, getAppTz } from '../theme';
import { funnelApi } from '../api';

// ── Ventanas ────────────────────────────────────────────────────────────────
// Las claves son EXACTAMENTE las que acepta el backend (WINDOW_KEYS). El `hint`
// dice la duración REAL: 'now' son 5 minutos en el backend (= LIVE_TTL del
// tracker de visitas), no "un instante". Mejor decirlo que dejar que el
// operador lo suponga.
const WINDOWS = [
  { key: 'now',   label: 'Ahora mismo',    hint: 'últimos 5 minutos' },
  { key: '10m',   label: 'Últimos 10 min', hint: 'últimos 10 minutos' },
  { key: '1h',    label: 'Última hora',    hint: 'última hora' },
  { key: 'today', label: 'Hoy',            hint: 'desde medianoche' },
  { key: '24h',   label: 'Últimas 24h',    hint: 'últimas 24 horas' },
];

const REFRESH_MS = 10000;

// ── Tipos de evento ─────────────────────────────────────────────────────────
// Los cuatro que emite el backend en `activity[].type`. `payment_failed` no
// estaba en la especificación original pero el backend lo manda (salvo que el
// tenant tenga ocultos los fallos), así que se contempla: un tipo sin mapear
// saldría como "—" y el operador no sabría qué está viendo.
const EVENT_TYPES = {
  active_visitor:     { label: 'Visitante en vivo',  short: 'En vivo',  color: '#38f0ff', dim: G.blueDim,   badge: 'badge-blue' },
  cart_created:       { label: 'Carrito creado',     short: 'Carrito',  color: G.blue,   dim: G.blueDim,   badge: 'badge-blue' },
  checkout_reached:   { label: 'Llegó al checkout',  short: 'Checkout', color: G.orange, dim: G.orangeDim, badge: 'badge-orange' },
  purchase_completed: { label: 'Compra completada',  short: 'Compra',   color: G.green,  dim: G.greenDim,  badge: 'badge-green' },
  payment_failed:     { label: 'Pago fallido',       short: 'Fallido',  color: G.red,    dim: G.redDim,    badge: 'badge-red' },
};
const UNKNOWN_EVENT = { label: 'Evento', short: 'Evento', color: G.muted, dim: 'transparent', badge: 'badge-gray' };
const evMeta = (t) => EVENT_TYPES[t] || UNKNOWN_EVENT;

// ── Helpers de formato ──────────────────────────────────────────────────────

// Hora con SEGUNDOS en la zona del panel. fmtDayTime() solo da hh:mm, y en un
// feed en vivo los segundos son justo lo que distingue dos eventos seguidos.
function clock(iso) {
  if (!iso) return '--:--:--';
  try {
    return new Date(iso).toLocaleTimeString('es-ES', {
      hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: getAppTz(),
    });
  } catch { return '--:--:--'; }
}

const num = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('es-ES') : '--');

// Porcentaje HONESTO: si no hay denominador el backend manda 0, y un 0 % ahí
// significa "no se puede calcular", no "cero por ciento". Se muestra "--".
function pctText(value, hasBase) {
  if (!hasBase) return '--';
  const n = Number(value);
  if (!Number.isFinite(n)) return '--';
  return `${n.toFixed(1)}%`;
}

const MONO = 'DM Mono, monospace';

// ── Estilos propios de la sección ───────────────────────────────────────────
// Scoped con prefijo lf- para no pisar nada de App.js. Incluye el bloque de
// prefers-reduced-motion: quien lo tenga activado no ve NINGUNA transición.
const CSS = `
.lf-num { transition: color .45s ease; }
.lf-num.lf-changed { color: ${G.goldBright} !important; }
.lf-bar { transition: width .45s ease; }
.lf-row { transition: background .3s ease; }
.lf-live { animation: lf-pulse 2.4s ease-in-out infinite; }
@keyframes lf-pulse { 0%,100% { opacity: 1 } 50% { opacity: .35 } }
.lf-main {
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(0, 1.6fr);
  gap: 16px;
  margin-top: 16px;
  align-items: start;
}
.lf-kpis {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.lf-kpi-wide { grid-column: 1 / -1; }
.lf-split {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
  gap: 16px;
  margin-top: 16px;
}
@media (max-width: 1100px) {
  .lf-main { grid-template-columns: 1fr; }
  .lf-split { grid-template-columns: minmax(0, 1fr); }
}
@media (max-width: 640px) {
  .lf-main { gap: 12px; margin-top: 12px; }
  .lf-kpis { gap: 6px; }
  .lf-kpi-val { font-size: 22px !important; }
  /* En móvil el globo es más bajo y la leyenda va ARRIBA (no encima del globo) */
  .lf-globe-card { min-height: 300px !important; }
  .lf-globe-wrap { min-height: 280px !important; }
  .lf-globe-legend {
    position: static !important;
    padding: 12px 14px 0;
    gap: 10px !important;
  }
  .lf-split { gap: 12px; margin-top: 12px; }
}
@media (prefers-reduced-motion: reduce) {
  .lf-num, .lf-bar, .lf-row { transition: none !important; }
  .lf-live { animation: none !important; }
}
`;

// ── Globo 3D interactivo (cobe, estilo Shopify/HurryPayments) ───────────────
// WebGL dot-globe: arrastrar para rotar, rueda/pellizco para zoom. Los eventos
// (compras/checkouts) se pintan como markers cian brillantes. Se degrada con
// gracia si no hay WebGL (algunos webviews), sin romper el panel.

function _hexToRgb(hex) {
  if (typeof hex !== 'string') return [0.4, 0.95, 1];
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const n = parseInt(h, 16);
  if (!Number.isFinite(n) || h.length !== 6) return [0.4, 0.95, 1];
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function _webglAvailable() {
  try {
    const c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
  } catch { return false; }
}

function DotGlobe({ geoPoints }) {
  const canvasRef = useRef(null);
  const markersRef = useRef([]);
  const pointerDownX = useRef(null);        // clientX at drag start (minus accumulated)
  const pointerMovement = useRef(0);        // accumulated horizontal drag delta
  const lastY = useRef(null);
  const thetaRef = useRef(0.28);            // vertical tilt (radians)
  const scaleRef = useRef(1);               // zoom 1..3
  const [webglOk] = useState(_webglAvailable);

  // Keep the markers ref fresh so the render loop picks up new events (polling
  // every 10s) WITHOUT recreating the globe (which would reset rotation/zoom).
  useEffect(() => {
    markersRef.current = (geoPoints || []).map(p => ({
      location: [p.lat, p.lng],
      size: 0.028 + Math.min(0.05, ((p.count || 1) - 1) * 0.006),
      color: _hexToRgb(p.color),
    }));
  }, [geoPoints]);

  useEffect(() => {
    if (!webglOk) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width = canvas.offsetWidth || 460;
    const onResize = () => { width = canvas.offsetWidth || width; };
    window.addEventListener('resize', onResize);

    let phi = 4.2;   // empieza sobre Atlántico/Europa/África
    let globe;
    try {
      globe = createGlobe(canvas, {
        devicePixelRatio: dpr,
        width: width * dpr,
        height: width * dpr,
        phi: 4.2,
        theta: thetaRef.current,
        dark: 1,
        diffuse: 1.35,
        mapSamples: 22000,
        mapBrightness: 8,
        baseColor: [0.20, 0.70, 0.86],
        markerColor: [0.45, 0.95, 1.0],
        glowColor: [0.13, 0.40, 0.62],
        opacity: 0.98,
        markers: markersRef.current,
        onRender: (state) => {
          if (pointerDownX.current === null) phi += 0.0035; // auto-rota en reposo
          state.phi = phi + pointerMovement.current / 200;
          state.theta = thetaRef.current;
          state.scale = scaleRef.current;
          state.markers = markersRef.current;
          state.width = width * dpr;
          state.height = width * dpr;
        },
      });
    } catch (e) {
      window.removeEventListener('resize', onResize);
      return undefined;
    }

    const onPointerDown = (e) => {
      pointerDownX.current = e.clientX - pointerMovement.current;
      lastY.current = e.clientY;
      canvas.style.cursor = 'grabbing';
    };
    const onPointerUp = () => {
      pointerDownX.current = null;
      lastY.current = null;
      canvas.style.cursor = 'grab';
    };
    const onPointerMove = (e) => {
      if (pointerDownX.current !== null) {
        pointerMovement.current = e.clientX - pointerDownX.current;
      }
      if (lastY.current !== null) {
        const dy = (e.clientY - lastY.current) / 220;
        thetaRef.current = Math.max(-0.6, Math.min(0.6, thetaRef.current + dy));
        lastY.current = e.clientY;
      }
    };
    const onWheel = (e) => {
      e.preventDefault();
      const next = scaleRef.current * (e.deltaY < 0 ? 1.12 : 0.89);
      scaleRef.current = Math.min(3, Math.max(1, next));
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      window.removeEventListener('resize', onResize);
      canvas.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('wheel', onWheel);
      if (globe) globe.destroy();
    };
  }, [webglOk]);

  if (!webglOk) {
    return (
      <div style={{
        width: '100%', height: '100%', minHeight: 400, display: 'flex',
        alignItems: 'center', justifyContent: 'center', color: '#4a5280',
        fontSize: 12, textAlign: 'center', padding: 20,
      }}>
        Mapa 3D no disponible en este navegador
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: '100%', maxWidth: 480, aspectRatio: '1 / 1', margin: '0 auto',
        display: 'block', cursor: 'grab', touchAction: 'none',
        contain: 'layout paint size',
      }}
    />
  );
}

// ── Valor que parpadea suavemente al cambiar ────────────────────────────────
// Un panel abierto horas no puede parpadear agresivamente. Esto es un cambio de
// color de 450 ms al oro y vuelta: se nota de reojo, no molesta, y respeta
// prefers-reduced-motion vía CSS.
function FlashNum({ value, style, className = '' }) {
  const [changed, setChanged] = useState(false);
  const prev = useRef(value);
  const timer = useRef(null);

  useEffect(() => {
    if (prev.current !== value) {
      prev.current = value;
      setChanged(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setChanged(false), 550);
    }
  }, [value]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return (
    <span className={`lf-num ${changed ? 'lf-changed' : ''} ${className}`.trim()} style={style}>
      {value}
    </span>
  );
}

// ── Componente ──────────────────────────────────────────────────────────────
export default function LiveFunnel({ showToast }) {
  const [win, setWin] = useState('10m');
  const [shopId, setShopId] = useState('');

  const [data, setData] = useState(null);
  const [shops, setShops] = useState([]);      // se conserva entre fallos de red
  const [loading, setLoading] = useState(true); // solo la primera carga
  const [err, setErr] = useState('');
  const [paused, setPaused] = useState(false);

  const [lastOkAt, setLastOkAt] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());

  // Del punto bajo el cursor guardamos solo el ID, no el objeto: así el tooltip
  // se repinta con los datos NUEVOS en cada refresco en vez de quedarse con la
  // foto de hace 10 s (y desaparece solo si el punto deja de existir).

  const inFlight = useRef(false);
  const reqSeq = useRef(0);
  const mounted = useRef(true);
  const errShown = useRef(false);               // para no repetir el toast cada 10 s

  // Se vuelve a poner a true en el cuerpo del efecto: React.StrictMode (index.js)
  // monta → desmonta → remonta en desarrollo, y si sólo el cleanup tocara el
  // ref, quedaría en false para siempre y se descartarían todas las respuestas.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // ── Carga ─────────────────────────────────────────────────────────────────
  // force = petición del usuario (cambio de ventana/tienda, botón refrescar):
  // adelanta a la que estuviera en vuelo. Sin force (polling) se respeta la
  // anterior y no se encola nada.
  const load = useCallback(async ({ force = false, first = false } = {}) => {
    if (inFlight.current && !force) return;
    const seq = ++reqSeq.current;
    inFlight.current = true;
    if (first) setLoading(true);

    try {
      const params = { window: win };
      if (shopId) params.shopId = shopId;
      const d = await funnelApi.live(params);

      if (!mounted.current || seq !== reqSeq.current) return; // respuesta vieja
      setData(d && typeof d === 'object' ? d : null);
      if (d && Array.isArray(d.shops)) setShops(d.shops.filter(Boolean));
      setErr('');
      errShown.current = false;
      setLastOkAt(Date.now());
    } catch (e) {
      if (!mounted.current || seq !== reqSeq.current) return;

      // 403 = el shopId no pertenece a esta cuenta (el backend valida el filtro
      // de tienda contra los shops del tenant). Volvemos a "todas" en vez de
      // dejar al operador reintentando un filtro imposible.
      const status = e && e.response && e.response.status;
      if (status === 403 && shopId) {
        setShopId('');
        setErr('Esa tienda ya no está disponible en tu cuenta. Volviendo a todas las tiendas.');
        if (showToast) showToast('Esa tienda ya no está disponible en tu cuenta', 'error');
        return;
      }

      setErr('No se pudo actualizar el panel en vivo.');
      // Un solo toast por racha de fallos: con polling cada 10 s, avisar en cada
      // intento llenaría la pantalla de toasts durante un corte de red.
      if (!errShown.current) {
        errShown.current = true;
        if (showToast) showToast('No se pudo cargar el panel en vivo', 'error');
      }
    } finally {
      if (seq === reqSeq.current) inFlight.current = false;
      if (mounted.current && first) setLoading(false);
    }
  }, [win, shopId, showToast]);

  // Siempre la última versión de load, para que el intervalo no se recree.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  // Carga inicial + recarga inmediata al cambiar ventana o tienda.
  const firstDone = useRef(false);
  useEffect(() => {
    const isFirst = !firstDone.current;
    firstDone.current = true;
    load({ force: true, first: isFirst });
  }, [load]);

  // Polling. Se limpia SIEMPRE en el cleanup y no corre con la pestaña oculta.
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (loadRef.current) loadRef.current();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [paused]);

  // Al volver al primer plano, refrescar ya: mientras estuvo oculta no se ha
  // pedido nada, así que los datos pueden ser de hace mucho.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVis = () => {
      if (!document.hidden && !paused && loadRef.current) loadRef.current();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [paused]);

  // Reloj del "actualizado hace Xs". No hace nada con la pestaña oculta para no
  // re-renderizar en segundo plano.
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      setNowMs(Date.now());
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // ── Derivados ─────────────────────────────────────────────────────────────
  const winMeta = WINDOWS.find(w => w.key === win) || WINDOWS[1];
  const kpis = (data && data.kpis) || {};
  const funnel = (data && Array.isArray(data.funnel)) ? data.funnel : [];
  const topProducts = (data && Array.isArray(data.topProducts)) ? data.topProducts : [];
  const activity = (data && Array.isArray(data.activity)) ? data.activity : [];
  const geo = (data && Array.isArray(data.geo)) ? data.geo : [];
  const tz = (data && data.timezone) || getAppTz();

  const agoSec = lastOkAt ? Math.max(0, Math.round((nowMs - lastOkAt) / 1000)) : null;

  // "Sin actividad" = no hay NADA que contar en la ventana. Se mira el total de
  // Base de las barras: la primera etapa (visitas). Si no hay visitas pero sí
  // etapas posteriores (posible: `visits` solo guarda 1 fila por sesión y día),
  // se cae al máximo para que las barras sigan siendo comparables entre sí.
  const funnelBase = useMemo(() => {
    const first = Number(funnel[0] && funnel[0].count) || 0;
    if (first > 0) return first;
    return funnel.reduce((m, s) => Math.max(m, Number(s.count) || 0), 0);
  }, [funnel]);

  const globePoints = useMemo(() => geo.map(p => {
    const lat = Number(p.lat);
    const lng = Number(p.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng, color: evMeta(p.type).color, count: Number(p.count) || 1 };
  }).filter(Boolean), [geo]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading && !data) {
    return (
      <div>
        <style>{CSS}</style>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 60, color: G.muted }}>
            Cargando panel en vivo…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>{CSS}</style>

      {/* ── Barra de control: ventana, tienda, pausa, frescura ───────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ paddingTop: 16, paddingBottom: 16 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="pill-group">
              {WINDOWS.map(w => (
                <button
                  key={w.key}
                  className={`pill ${win === w.key ? 'active' : ''}`}
                  onClick={() => { if (win !== w.key) setWin(w.key); }}
                  title={`Ventana: ${w.hint}`}
                >
                  {w.label}
                </button>
              ))}
            </div>

            <select
              className="form-input"
              style={{ width: 'auto', minWidth: 190, padding: '7px 30px 7px 12px', fontSize: 12 }}
              value={shopId}
              onChange={e => setShopId(e.target.value)}
              title="Filtrar por tienda"
            >
              <option value="">Todas las tiendas</option>
              {shops.map(s => (
                <option key={s.id} value={s.id}>{s.name || s.id}</option>
              ))}
            </select>

            <div style={{ flex: 1 }} />

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, color: G.muted, fontFamily: MONO }}>
                <span
                  className={paused ? '' : 'lf-live'}
                  style={{
                    width: 7, height: 7, borderRadius: '50%',
                    background: paused ? G.muted : (err ? G.red : G.green),
                    display: 'inline-block',
                  }}
                />
                {paused
                  ? 'En pausa'
                  : agoSec === null ? 'Sin datos aún'
                  : agoSec < 2 ? 'actualizado ahora'
                  : `actualizado hace ${agoSec}s`}
              </span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setPaused(p => !p)}
                title={paused ? 'Reanudar el refresco automático' : 'Pausar el refresco automático'}
              >
                {paused ? '▶ Reanudar' : '❚❚ Pausar'}
              </button>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => load({ force: true })}
                title="Actualizar ahora"
              >
                ↻ Actualizar
              </button>
            </div>
          </div>

          <div style={{ marginTop: 10, fontSize: 11, color: G.muted, fontFamily: MONO }}>
            {win === 'today' && data && data.windowLabel
              ? data.windowLabel
              : `${winMeta.label} · ${winMeta.hint}`}
            {' · '}{tz}
            {data && data.shopName ? ` · ${data.shopName}` : ''}
            {data && data.generatedAt ? ` · generado ${clock(data.generatedAt)}` : ''}
          </div>

          {err && (
            <div style={{
              marginTop: 10, padding: '8px 12px', borderRadius: 8,
              background: G.redDim, border: `1px solid ${G.red}44`,
              fontSize: 12, color: G.red,
            }}>
              {err} Se sigue reintentando cada {REFRESH_MS / 1000}s; los datos de abajo son los últimos correctos.
            </div>
          )}
        </div>
      </div>

      {/* ── Layout HP: KPIs+Funnel izquierda, Globo derecha ─────────────── */}
      <div className="lf-main">

        {/* ── COLUMNA IZQUIERDA ─────────────────────────────────────────────── */}
        <div>
          {/* KPIs 2x2 + conversión ancho completo */}
          <div className="card">
            <div className="card-body" style={{ padding: 16 }}>
              <div className="lf-kpis">
                <div style={{ padding: '10px 0' }}>
                  <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Active visitors</div>
                  <div className="lf-kpi-val" style={{ fontSize: 28, color: G.white, fontWeight: 700, fontFamily: MONO }}><FlashNum value={num(kpis.activeVisitors)} /></div>
                </div>
                <div style={{ padding: '10px 0' }}>
                  <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Revenue</div>
                  <div className="lf-kpi-val" style={{ fontSize: 28, color: G.gold, fontWeight: 700, fontFamily: MONO }}><FlashNum value={fmt(Number(kpis.revenue) || 0)} /></div>
                </div>
                <div style={{ padding: '10px 0' }}>
                  <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Reached checkout</div>
                  <div className="lf-kpi-val" style={{ fontSize: 28, color: G.white, fontWeight: 700, fontFamily: MONO }}><FlashNum value={num(kpis.reachedCheckout)} /></div>
                </div>
                <div style={{ padding: '10px 0' }}>
                  <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Completed purchases</div>
                  <div className="lf-kpi-val" style={{ fontSize: 28, color: G.green, fontWeight: 700, fontFamily: MONO }}><FlashNum value={num(kpis.completedPurchases)} /></div>
                </div>
                <div className="lf-kpi-wide" style={{ padding: '10px 0', borderTop: `1px solid ${G.border}` }}>
                  <div style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Conversion (checkout → purchase)</div>
                  <div className="lf-kpi-val" style={{ fontSize: 28, color: Number(kpis.reachedCheckout) > 0 ? G.blue : G.muted, fontWeight: 700, fontFamily: MONO }}>
                    <FlashNum value={pctText(kpis.conversionRate, Number(kpis.reachedCheckout) > 0)} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Embudo */}
          <div className="card" style={{ marginTop: 12 }}>
            <div className="card-head">
              <div className="card-title">Funnel</div>
              <div style={{ fontSize: 11, color: G.muted, fontFamily: MONO }}>drop-off vs previous stage</div>
            </div>
            <div className="card-body">
              {funnel.length === 0 ? (
                <div className="empty"><div>No stages in this window</div></div>
              ) : funnel.map((s, i) => {
                const count = Number(s.count) || 0;
                const width = funnelBase > 0 ? Math.min(100, (count / funnelBase) * 100) : 0;
                const prevCount = i > 0 ? (Number(funnel[i - 1].count) || 0) : null;
                const drop = s.dropOffPct;
                const hasDrop = i > 0 && prevCount > 0 && Number.isFinite(Number(drop));
                const d = Number(drop);
                let dropColor = G.muted;
                if (hasDrop) {
                  if (d < 0) dropColor = G.blue;
                  else if (d >= 40) dropColor = G.red;
                  else if (d >= 15) dropColor = G.orange;
                  else dropColor = G.dim;
                }
                return (
                  <div key={s.stage || i} style={{ marginBottom: i === funnel.length - 1 ? 0 : 14 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
                      <span style={{ fontSize: 13, color: G.white, fontWeight: 600 }}>{s.label || s.stage}</span>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, whiteSpace: 'nowrap' }}>
                        {i > 0 && (
                          <span style={{ fontSize: 11, color: dropColor, fontFamily: MONO, fontWeight: 600 }}>
                            {!hasDrop ? '--' : d < 0 ? `+${Math.abs(d).toFixed(1)}%` : `${d.toFixed(1)}%`}
                          </span>
                        )}
                        <span style={{ fontSize: 16, color: G.white, fontFamily: MONO, fontWeight: 700, minWidth: 40, textAlign: 'right' }}>
                          <FlashNum value={num(count)} />
                        </span>
                      </div>
                    </div>
                    <div className="progress" style={{ height: 8 }}>
                      <div className="progress-fill lf-bar" style={{
                        width: `${width}%`,
                        background: i === funnel.length - 1
                          ? `linear-gradient(90deg, ${G.green}, ${G.green}cc)`
                          : `linear-gradient(90deg, ${G.gold}, ${G.goldLight})`,
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Productos más vendidos */}
          <div className="card" style={{ marginTop: 12, minWidth: 0 }}>
            <div className="card-head">
              <div className="card-title">Best-selling products</div>
            </div>
            {topProducts.length === 0 ? (
              <div className="card-body"><div className="empty"><div>No sales yet</div></div></div>
            ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Producto</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>Uds</th>
                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>Ingresos</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.map((p, i) => (
                    <tr key={`${p.name}-${i}`}>
                      <td style={{ color: G.white, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.name}>
                        {p.name || '--'}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: MONO }}>{num(p.units)}</td>
                      <td style={{ textAlign: 'right', fontFamily: MONO, color: G.gold }}>
                        {Number.isFinite(Number(p.revenue)) ? fmt(Number(p.revenue)) : '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </div>

          {/* Actividad en vivo */}
          <div className="card" style={{ marginTop: 12, minWidth: 0 }}>
            <div className="card-head">
              <div className="card-title">Live Activity</div>
              <div style={{ fontSize: 11, color: G.muted, fontFamily: MONO }}>
                {activity.length > 0 ? `${activity.length} events` : ''}
              </div>
            </div>
            {activity.length === 0 ? (
              <div className="card-body"><div className="empty"><div>No activity yet</div></div></div>
            ) : (
              <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                {activity.map((a, i) => {
                  const meta = evMeta(a.type);
                  const place = [a.city, a.country].filter(Boolean).join(', ');
                  return (
                    <div
                      key={`${a.at || 'x'}-${a.type}-${i}`}
                      className="lf-row"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 16px',
                        borderBottom: i === activity.length - 1 ? 'none' : `1px solid ${G.border}`,
                        borderLeft: `2px solid ${meta.color}`,
                      }}
                    >
                      {place && (
                        <span style={{ fontSize: 11, color: G.dim, whiteSpace: 'nowrap', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }} title={place}>
                          {place}
                        </span>
                      )}
                      <span style={{ flex: 1 }} />
                      <span style={{ fontSize: 11, color: G.white, whiteSpace: 'nowrap' }}>
                        {meta.short}{a.label ? ` · ${a.label}` : ''}
                      </span>
                      <span style={{ fontSize: 12, fontFamily: MONO, color: meta.color, whiteSpace: 'nowrap', minWidth: 60, textAlign: 'right' }}>
                        {Number.isFinite(Number(a.amount)) && Number(a.amount) > 0 ? fmt(Number(a.amount)) : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── COLUMNA DERECHA: Globo 3D ────────────────────────────────────── */}
        <div className="card lf-globe-card" style={{ position: 'relative', overflow: 'hidden', background: 'linear-gradient(180deg, #0a0e27 0%, #0d1333 40%, #111940 100%)', minHeight: 460 }}>
          <div className="lf-globe-legend" style={{ position: 'absolute', top: 16, left: 20, zIndex: 2, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {['active_visitor', 'checkout_reached', 'purchase_completed', 'payment_failed'].map(t => (
              <span key={t} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: '#8090b0' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: evMeta(t).color, display: 'inline-block', boxShadow: `0 0 6px ${evMeta(t).color}88` }} />
                {evMeta(t).label}
              </span>
            ))}
          </div>
          <div className="lf-globe-wrap" style={{ width: '100%', height: '100%', minHeight: 460 }}>
            <DotGlobe geoPoints={globePoints} />
          </div>
        </div>
      </div>
    </div>
  );
}
